import Modal from "components/Modal";
import { useEffect, useState } from "react";
import { AddFacilityLogicRetVal } from "./useAddFacilityLogic.hook";
import { FormProvider } from "react-hook-form";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import { KommunkodDropdown } from "components/KommunkodDropdown";
import Divider from "components/Divider";
import { TextAreaFormProvider } from "components/TextAreaFormProvider";
import SecondaryButton from "components/buttons/SecondaryButton";
import { Tips } from "components/Tips";

type Props = {
  logic: AddFacilityLogicRetVal;
};
export const AddFacilityModal = ({ logic }: Props) => {
  const postnummer = logic.form.watch("postalCode");

  const [arbetsstalleSearch, setArbetsstalleSearch] = useState("");
  useEffect(() => {
    if (!logic.isModalOpen) {
      setArbetsstalleSearch("");
    }
  }, [logic.isModalOpen]);

  const showArbetsstalleSearch = logic.availableArbetsstallen.length > 20;
  const searchTerm = arbetsstalleSearch.trim().toLowerCase();
  const filteredArbetsstallen = searchTerm
    ? logic.availableArbetsstallen.filter((a) =>
        [a.name, a.address, a.city, a.cfarNr]
          .filter(Boolean)
          .some((field) => field!.toLowerCase().includes(searchTerm)),
      )
    : logic.availableArbetsstallen;

  return (
    <Modal
      isOpen={logic.isModalOpen}
      title={"Ny anläggning - " + logic.customer?.name}
      actionText="Spara"
      onClose={logic.onModalClose}
      afterClose={logic.afterModalClose}
      actionHandler={() => console.log("hej")}
      isLoading={logic.mutation.isPending}
      loadingText="Sparar..."
      hideButtons
      isWide
    >
      <FormProvider {...logic.form}>
        <form className="mt-4" onSubmit={logic.onSubmit}>
          {logic.availableArbetsstallen.length > 0 && (
            <div className="mb-4 rounded-md border border-cyan-200 bg-cyan-50 p-3">
              <label className="block text-sm font-semibold text-gray-950">
                Hämta från SCB:s företagsregister
              </label>
              <p className="mb-2 mt-0.5 text-xs text-gray-600">
                Välj ett arbetsställe så fylls fälten i automatiskt. Du kan även
                fylla i manuellt nedan.
              </p>
              {showArbetsstalleSearch && (
                <input
                  type="text"
                  value={arbetsstalleSearch}
                  onChange={(e) => setArbetsstalleSearch(e.target.value)}
                  placeholder="Filtrera på namn, adress eller ort..."
                  className="mb-2 w-full rounded-md border-gray-300 text-sm"
                />
              )}
              <select
                className="w-full rounded-md border-gray-300 text-sm"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    logic.applyArbetsstalle(e.target.value);
                  }
                }}
              >
                <option value="">
                  {filteredArbetsstallen.length ===
                  logic.availableArbetsstallen.length
                    ? `— Välj arbetsställe (${logic.availableArbetsstallen.length} st) —`
                    : `— Välj arbetsställe (${filteredArbetsstallen.length} av ${logic.availableArbetsstallen.length} träffar) —`}
                </option>
                {filteredArbetsstallen.map((a) => (
                  <option key={a.cfarNr} value={a.cfarNr}>
                    {a.name ?? a.cfarNr}
                    {a.address ? ` – ${a.address}` : ""}
                    {a.city ? `, ${a.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mt-2 grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
            <TextInputFormProvider
              required
              label="Anläggningens namn"
              name="name"
              labelClassName="font-semibold"
            />
            <div className="hidden sm:block"></div>
            <TextInputFormProvider
              label="Epostadress för rapporter"
              name="documentationEmail"
              labelClassName="font-semibold"
              info="Behöver bara anges om den skiljer sig från kundens"
            />
            <div className="hidden sm:block"></div>
          </div>
          <Divider className="mb-4 mt-6" />
          <h4 className="flex items-center justify-between text-sm font-bold text-gray-950">
            Adressuppgifter
            <SecondaryButton size="small" onClick={logic.copyCustomerAddress}>
              Kopiera från kund
            </SecondaryButton>
          </h4>
          <div className="mb-4 mt-2 grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-4">
            <TextInputFormProvider label="Gata och gatunummer" name="address" />
            <TextInputFormProvider label="Postnummer" name="postalCode" />
            <TextInputFormProvider label="Postort" name="city" />
            <KommunkodDropdown
              postnummer={postnummer}
              name="kommunKod"
              register={logic.form.register}
              setKommunKod={(k) => logic.form.setValue("kommunKod", k)}
              setPostalCity={logic.applyGuessedCity}
            />

            <TextInputFormProvider
              label="CFAR-nummer (för ombudsrapportering)"
              name="cfarNr"
            />
            <div></div>
          </div>

          <Tips
            className="mb-4"
            id="793e06be-66de-4a19-b8a4-1174c9a7b77e"
            paragraphs={[
              "Om platsen saknar adress kan du istället ange en exakt position med koordinater. För rapportering till Avfallsregistret krävs antingen adress eller koordinater. Öppna Google Maps, högerklicka på platsen och klicka på koordinaterna så kopieras de till urklipp. Klistra sedan in dem nedan. ",
            ]}
          />
          <TextInputFormProvider
            name="coordinates"
            label="Koordinater"
            placeholder="xx.xxxxxxxxxxxxxxx, yy.yyyyyyyyyyyyyyy"
            link="https://www.google.se/maps/"
            linkLabel="Öppna Google Maps"
          />

          <Divider className="mb-4 mt-6" />

          <h4 className="mt-6 flex items-center justify-between text-sm font-bold text-gray-950">
            Kontaktperson
            <SecondaryButton size="small" onClick={logic.copyCustomerContact}>
              Kopiera från kund
            </SecondaryButton>
          </h4>

          <div className="mt-2 grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-4">
            <TextInputFormProvider label="Namn" name="contactName" />
            <TextInputFormProvider label="Telefon" name="contactPhone" />
            <TextInputFormProvider label="Epost" name="contactEmail" />
          </div>
          <Divider className="mb-4 mt-6" />
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextAreaFormProvider label="Anteckningar" name="notes" rows={3} />
            <div></div>
          </div>
          <Divider className="mb-4 mt-6" />
          <div className="flex justify-end">
            <div className="flex w-96 gap-x-2">
              <ModalSecondaryAction onClick={logic.onModalClose} />
              <ModalPrimaryButton
                isLoading={logic.mutation.isPending}
                buttonText="Spara"
                loadingText="Sparar"
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

type ModalPrimaryButtonProps = {
  isLoading?: boolean;
  isDisabled?: boolean;
  type?: "submit" | "button";
  loadingText?: string;
  buttonText?: string;
  onClick?: () => void;
};
export const ModalPrimaryButton = ({
  isLoading = false,
  isDisabled = false,
  type = "submit",
  loadingText = "Sparar...",
  buttonText = "Spara",
  onClick,
}: ModalPrimaryButtonProps) => (
  <button
    type={type}
    className="inline-flex w-full justify-center rounded-md border border-transparent bg-cyan-700 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 disabled:bg-gray-400 sm:col-start-2 sm:text-sm"
    onClick={onClick}
    disabled={isDisabled || isLoading}
  >
    {isLoading && (
      <svg
        className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    )}

    {isLoading ? loadingText : buttonText}
  </button>
);

type ModalSecondaryButtonProps = {
  onClick: () => void;
  text?: string;
};
export const ModalSecondaryAction = ({
  onClick,
  text = "Avbryt",
}: ModalSecondaryButtonProps) => (
  <button
    type="button"
    className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
    onClick={onClick}
  >
    {text}
  </button>
);
